/**
 * The match-area rule, in one place: the ceiling that doubles as the "no upper
 * limit" sentinel, and the check every locate method applies to a match's
 * measured area.
 *
 * The skill-config schemas here, the vision runtime that enforces them, routine
 * validation and the wizard all need this, so it sits where none of them has to
 * import another's surface to get it. It imports nothing.
 */

/**
 * Upper bound of a locate method's match-area range, in cm² on the surface the
 * match sits on. Also the "unbounded" sentinel: a configured max at/above this
 * value means "no upper limit".
 */
export const LOCATE_MAX_MATCH_AREA_CM2 = 500;

export function isUnboundedMaxMatchAreaCm2(maxAreaCm2: number): boolean {
  return maxAreaCm2 >= LOCATE_MAX_MATCH_AREA_CM2;
}

export interface MatchAreaBounds {
  minMatchAreaCm2?: number;
  maxMatchAreaCm2?: number;
}

/**
 * Whether a method's bounds can drop anything. Absent bounds, a zero min and a
 * max at the ceiling all keep every match, so the runtime can skip measuring.
 */
export function hasMatchAreaBounds(bounds: MatchAreaBounds): boolean {
  return (
    (bounds.minMatchAreaCm2 ?? 0) > 0 ||
    (bounds.maxMatchAreaCm2 != null &&
      !isUnboundedMaxMatchAreaCm2(bounds.maxMatchAreaCm2))
  );
}

/**
 * Whether a measured area falls inside a method's bounds. A missing bound is
 * the open one, so a skill saved before the bounds existed keeps every match.
 */
export function isMatchAreaWithinBounds(
  areaCm2: number,
  bounds: MatchAreaBounds,
): boolean {
  const minAreaCm2 = bounds.minMatchAreaCm2 ?? 0;
  const maxAreaCm2 = bounds.maxMatchAreaCm2 ?? LOCATE_MAX_MATCH_AREA_CM2;

  return (
    areaCm2 >= minAreaCm2 &&
    (isUnboundedMaxMatchAreaCm2(maxAreaCm2) || areaCm2 <= maxAreaCm2)
  );
}
